import React, { useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import CurrentCustomer from 'context/global-customer-creation/context';
import CoOwnersManagement from 'context/co-owners-management/context';
import documentUploadService from 'service/document-updload';
import {
  deleteUploadedFilesService,
  updateExpirationDateService,
} from 'service/download-uploaded-files';
import { arrayValidation } from 'utils/array';
import { formatDate } from 'utils/date';
import type { DocumentData } from 'commons/modules/service/documents-status';
import type { DocumentFile } from 'components/NewClient/Onboarding/DocumentUpload/UploadFiles/models/document-file';
import { initialFilesMap } from 'components/NewClient/Onboarding/DocumentUpload/UploadFiles/constants';
import DocumentSaveSteps from './context';

interface Props {
  children: React.ReactNode;
}

const DocumentSaveStepsProvider = ({ children }: Props) => {
  const { pathname } = useLocation();
  const { currentCustomer } = useContext(CurrentCustomer);
  const { coOwnerData } = useContext(CoOwnersManagement);
  const { clientId, contractId } = currentCustomer;

  const [uploadingFile, setUploadingFile] = useState(false);
  const [files, setFiles] = useState<DocumentFile>({});

  useEffect(() => {
    setFiles(
      pathname === '/clientes/nuevo' ||
        String(pathname).includes('/clientes/fisica/')
        ? initialFilesMap.fisica
        : initialFilesMap.moral,
    );
  }, [pathname]);

  const saveFile = async (file: any, name: string) => {
    const coOwnerId = coOwnerData?.coOwnerId || 0;
    const { success, errorMessage } = await documentUploadService({
      clientId: `${clientId || 0}`,
      contractId: `${contractId}`,
      step: files[name].step,
      file: file as any,
      coOwnerId,
    });
    setUploadingFile(false);
    setFiles({
      ...files,
      [name]: {
        file,
        fileList: [
          ...files[name].fileList,
          ...file.map(({ name: fileName }: any) => fileName),
        ],
        uploaded: success,
        error: errorMessage,
        uploading: false,
      },
    } as any);
  };

  const handleFile = async ({
    fileList,
    name,
  }: {
    fileList: DocumentFile;
    name: string;
  }) => {
    if (fileList) {
      setUploadingFile(true);
      await saveFile(fileList, name);
    }
  };

  const handleDeleteFile = async ({
    file,
    name,
  }: {
    file: DocumentData;
    name: string;
  }) => {
    try {
      const response = await deleteUploadedFilesService(file.documentId);
      if (response && response.data) {
        const item = { ...files[name] };
        let fileList: any[] = [];
        if (files[name] && arrayValidation(files[name].fileList)) {
          fileList = files[name].fileList.filter(
            (itemFile: any) => itemFile.documentId !== file.documentId,
          );
        }
        item.fileList = fileList;
        setFiles({
          ...files,
          [name]: item,
        });
      }
      return true;
    } catch (error) {
      console.error(`No se pudieron obtener todos los archivos: ${error}`);
      return false;
    }
  };
  const handleUpdateExpirationDate = async (
    name: string,
    file: DocumentData,
    expirationDate: Date,
  ) => {
    try {
      const date = expirationDate
        ? formatDate(expirationDate, 'yyyy-MM-dd')
        : null;
      const response = await updateExpirationDateService(file.documentId, date);
      if (response && response.data) {
        const item = {
          ...files[name],
        };

        let fileList = item.fileList || [];
        fileList = fileList.map((fileItem: any) => {
          if (fileItem.documentId === file.documentId) {
            return {
              ...fileItem,
              expirationDate,
            };
          }
          return fileItem;
        });

        item.fileList = fileList;

        setFiles({
          ...files,
          [name]: item,
        });
      }
      return true;
    } catch (error) {
      console.error(`No se pudo actualizar la fecha: ${error}`);
      return false;
    }
  };

  return (
    <DocumentSaveSteps.Provider
      value={{
        files,
        uploadingFile,
        setFiles,
        handleFile,
        saveFile,
        handleDeleteFile,
        handleUpdateExpirationDate,
      }}
    >
      {children}
    </DocumentSaveSteps.Provider>
  );
};

export default DocumentSaveStepsProvider;
